import React, { Dispatch } from 'react';
import { Box, styled, TableRow, Typography } from '@mui/material';
import { CaseProcedureModel } from '../../../../lib/models/CaseProcedureModel';
import { DecisionReasonModel } from '../../../../lib/models/DecisionReasonModel';
import theme from '../../../../theme';
import { useScreenSmallerThan } from '../../../../lib/hooks/breakpoint-hooks';
import {
    KeyboardArrowContainer,
    StyledKeyboardArrowDownIcon,
    StyledKeyboardArrowUpOutlinedIcon
} from '../../../../styledComponents';
import { BoxRow, NormalBox, TableDataCell, TypographyCell } from './StyledBoxCell';
import ApprovalButtonsDecisionsContainer from './ApprovalButtonsDecisionsContainer';

export type InitialReviewProcedureItemProps = {
    item: CaseProcedureModel;
    caseStage: string;
    crmClientName?: string;
    reasons: DecisionReasonModel[] | null;
    handleReasonDenialChange: ScriptEvent;
    setAnyChangeMade: Dispatch<boolean>;
};

type ScriptEvent = () => void;

export enum ApprovalStatuses {
    Approved = 'Approved',
    PartiallyApproved = 'Partially Approved',
    Denied = 'Denied',
    NoReviewRequired = 'No Review Required'
}

function setApprovalStatusFromItem(item: CaseProcedureModel) {
    if (item.isApproved !== '0' && item.isApproved !== '1') {
        return item.isApproved;
    }

    if (item.isApproved === '1' && item.approvedUnits === item.requestedUnits) {
        return ApprovalStatuses.Approved;
    } else if (item.isApproved === '0' && !item.approvedUnits) {
        return ApprovalStatuses.Denied;
    } else {
        return ApprovalStatuses.PartiallyApproved;
    }
}

function InitialReviewProcedureItem({
    item,
    reasons,
    crmClientName,
    caseStage,
    handleReasonDenialChange,
    setAnyChangeMade
}: InitialReviewProcedureItemProps) {
    const [approvalStatus, setApprovalStatus] = React.useState<string | null>(() =>
        setApprovalStatusFromItem(item)
    );
    const [approvedUnits, setapprovedUnits] = React.useState(
        item.approvedUnits ? item.approvedUnits : 0
    );

    const [isNewCase, setIsNewCase] = React.useState<boolean>(
        item.isNewCode === null && item.deniedQuantity === null ? true : false
    );

    const isSmallerThanSmall = useScreenSmallerThan('sm');
    const isSmallerThanLarge = useScreenSmallerThan('lg');

    const TypographyHeader = styled(Typography)({
        color: theme.palette.cardSubtitle.main,
        fontSize: 13,
        width: !isSmallerThanSmall && !isSmallerThanLarge ? '50%' : '25%'
    });

    React.useEffect(() => {
        setIsNewCase(item.approvedUnits === null && item.deniedQuantity === null ? true : false);
    }, [approvedUnits, item.deniedQuantity, item.approvedUnits]);

    const handleApprove = () => {
        setAnyChangeMade(true);
        setApprovalStatus(ApprovalStatuses.Approved);
        item.isApproved = ApprovalStatuses.Approved;
        item.approvedUnits = item.requestedUnits;
        setapprovedUnits(item.approvedUnits);
        item.decisionReasons = [];
        item.deniedQuantity = 0;
        setIsNewCase(false);
    };

    const handlePartialApprove = () => {
        setAnyChangeMade(true);
        setApprovalStatus(ApprovalStatuses.PartiallyApproved);
        item.isApproved = ApprovalStatuses.PartiallyApproved;
        item.approvedUnits =
            approvedUnits === 0 || approvedUnits === item.requestedUnits ? 1 : approvedUnits;
        setapprovedUnits(item.approvedUnits);
        item.deniedQuantity = item.requestedUnits - item.approvedUnits;
        setIsNewCase(false);
    };

    const handleDeny = () => {
        setAnyChangeMade(true);
        setApprovalStatus(ApprovalStatuses.Denied);
        item.isApproved = ApprovalStatuses.Denied;
        item.approvedUnits = 0;
        setapprovedUnits(item.approvedUnits);
        item.deniedQuantity = item.requestedUnits;
        setIsNewCase(false);
    };

    const handleNoReviewRequired = () => {
        setAnyChangeMade(true);
        setApprovalStatus(ApprovalStatuses.NoReviewRequired);
        item.isApproved = ApprovalStatuses.NoReviewRequired;
        item.approvedUnits = 0;
        setapprovedUnits(item.approvedUnits);
        item.deniedQuantity = 0;
        setIsNewCase(false);
    };

    const increaseApproved = () => {
        setAnyChangeMade(true);
        setIsNewCase(false);
        if (item.approvedUnits == null) {
            item.approvedUnits = 0;
        }
        if (item.approvedUnits < item.requestedUnits) {
            item.approvedUnits++;
            setapprovedUnits(item.approvedUnits);
            item.deniedQuantity = item.requestedUnits - item.approvedUnits;
            item.isApproved = ApprovalStatuses.PartiallyApproved;
            setApprovalStatus(ApprovalStatuses.PartiallyApproved);
        }
        if (item.approvedUnits === item.requestedUnits) {
            item.isApproved = ApprovalStatuses.Approved;
            item.decisionReasons = [];
            setApprovalStatus(ApprovalStatuses.Approved);
        }
    };

    const decreaseApproved = () => {
        setAnyChangeMade(true);
        setIsNewCase(false);
        if (item.approvedUnits == null) {
            item.approvedUnits = 0;
        }
        if (item.deniedQuantity == null) {
            item.deniedQuantity = item.requestedUnits;
        }

        if (item.approvedUnits > 0) {
            item.approvedUnits--;
            setapprovedUnits(item.approvedUnits);
            item.deniedQuantity = item.requestedUnits - item.approvedUnits;
        }

        if (item.approvedUnits === 0) {
            item.isApproved = ApprovalStatuses.Denied;
            setApprovalStatus(ApprovalStatuses.Denied);
        } else {
            item.isApproved = ApprovalStatuses.PartiallyApproved;
            setApprovalStatus(ApprovalStatuses.PartiallyApproved);
        }
    };

    return !isSmallerThanSmall && !isSmallerThanLarge ? (
        <>
            <TableRow>
                <TableDataCell>
                    <TypographyCell>{item.requestedCode}</TypographyCell>
                </TableDataCell>
                <TableDataCell>
                    <Box sx={{ wordBreak: 'break-word' }}>{item.description}</Box>
                </TableDataCell>
                <TableDataCell sx={{ minWidth: '150px', display: 'flex', flexDirection: 'column' }}>
                    <NormalBox>
                        <TypographyCell>Requested:</TypographyCell> {item.requestedUnits}
                    </NormalBox>
                    {item.requestedUnits > 1 && (
                        <NormalBox sx={{ lineHeight: '2rem' }}>
                            <TypographyCell>Approved:</TypographyCell> {approvedUnits}
                            <KeyboardArrowContainer>
                                <StyledKeyboardArrowUpOutlinedIcon
                                    onClick={() =>
                                        increaseApproved()
                                    }></StyledKeyboardArrowUpOutlinedIcon>
                                <StyledKeyboardArrowDownIcon
                                    onClick={() =>
                                        decreaseApproved()
                                    }></StyledKeyboardArrowDownIcon>
                            </KeyboardArrowContainer>
                        </NormalBox>
                    )}
                </TableDataCell>
                <TableDataCell>
                    <Box>See nurse review above</Box>
                </TableDataCell>
                <TableDataCell sx={{ minWidth: '420px' }}>
                    <ApprovalButtonsDecisionsContainer
                        approvalStatus={approvalStatus}
                        isNewCase={isNewCase}
                        handleApprove={handleApprove}
                        handlePartialApprove={handlePartialApprove}
                        handleDeny={handleDeny}
                        handleNoReviewRequired={handleNoReviewRequired}
                        item={item}
                        reasons={reasons}
                        handleReasonDenialChange={handleReasonDenialChange}
                    />
                </TableDataCell>
            </TableRow>
        </>
    ) : isSmallerThanLarge && !isSmallerThanSmall ? (
        <Box>
            <BoxRow>
                <TypographyHeader>Requested Code</TypographyHeader>
                <TypographyHeader>Description</TypographyHeader>
                <TypographyHeader>Units</TypographyHeader>
            </BoxRow>
            <BoxRow>
                <TypographyCell sx={{ width: '25%' }}>{item.requestedCode}</TypographyCell>
                <Typography sx={{ width: '25%', wordBreak: 'break-word' }}>
                    {item.description}
                </Typography>
                <Box sx={{ width: '25%', display: 'flex', flexDirection: 'column' }}>
                    <NormalBox>
                        <TypographyCell>Requested:</TypographyCell> {item.requestedUnits}
                    </NormalBox>
                    {item.requestedUnits > 1 && (
                        <NormalBox sx={{ lineHeight: '2rem' }}>
                            <TypographyCell>Approved:</TypographyCell> {approvedUnits}
                            <KeyboardArrowContainer>
                                <StyledKeyboardArrowUpOutlinedIcon
                                    onClick={() =>
                                        increaseApproved()
                                    }></StyledKeyboardArrowUpOutlinedIcon>
                                <StyledKeyboardArrowDownIcon
                                    onClick={() =>
                                        decreaseApproved()
                                    }></StyledKeyboardArrowDownIcon>
                            </KeyboardArrowContainer>
                        </NormalBox>
                    )}
                </Box>
            </BoxRow>

            <BoxRow>
                <TypographyHeader sx={{ width: '35%' }}>Nurse Recommendation</TypographyHeader>
                <Box display={'inline-flex'} sx={{ width: '62.5%' }}>
                    <TypographyHeader sx={{ width: 'fit-content' }}>
                        Physician Determination
                    </TypographyHeader>
                    <Typography sx={{ color: 'red', paddingLeft: '2px' }}>*</Typography>
                </Box>
            </BoxRow>
            <BoxRow sx={{ marginBottom: '14px' }}>
                <Typography sx={{ width: '59%' }}>See nurse review above</Typography>
                <ApprovalButtonsDecisionsContainer
                    approvalStatus={approvalStatus}
                    isNewCase={isNewCase}
                    handleApprove={handleApprove}
                    handlePartialApprove={handlePartialApprove}
                    handleDeny={handleDeny}
                    handleNoReviewRequired={handleNoReviewRequired}
                    item={item}
                    reasons={reasons}
                    handleReasonDenialChange={handleReasonDenialChange}
                />
            </BoxRow>
        </Box>
    ) : (
        <>
            <BoxRow>
                <TypographyHeader sx={{ width: '50%' }}>Requested Code</TypographyHeader>
                <TypographyHeader sx={{ width: '50%' }}>Description</TypographyHeader>
            </BoxRow>
            <BoxRow>
                <TypographyCell sx={{ width: '50%' }}>{item.requestedCode}</TypographyCell>
                <Typography sx={{ width: '50%', wordBreak: 'break-word' }}>
                    {item.description}
                </Typography>
            </BoxRow>
            <BoxRow>
                <TypographyHeader sx={{ width: '50%' }}>Units</TypographyHeader>
                <TypographyHeader sx={{ width: '50%' }}>Nurse Recommendation</TypographyHeader>
            </BoxRow>
            <BoxRow>
                <Box sx={{ width: '50%', display: 'flex', flexDirection: 'column' }}>
                    <NormalBox>
                        <TypographyCell>Requested:</TypographyCell> {item.requestedUnits}
                    </NormalBox>
                    {item.requestedUnits > 1 && (
                        <NormalBox sx={{ lineHeight: '2rem' }}>
                            <TypographyCell>Approved:</TypographyCell> {approvedUnits}
                            <KeyboardArrowContainer>
                                <StyledKeyboardArrowUpOutlinedIcon
                                    onClick={() =>
                                        increaseApproved()
                                    }></StyledKeyboardArrowUpOutlinedIcon>
                                <StyledKeyboardArrowDownIcon
                                    onClick={() =>
                                        decreaseApproved()
                                    }></StyledKeyboardArrowDownIcon>
                            </KeyboardArrowContainer>
                        </NormalBox>
                    )}
                </Box>
                <Typography sx={{ width: '50%' }}>See nurse review above</Typography>
            </BoxRow>
            <BoxRow>
                <Box display={'inline-flex'}>
                    <TypographyHeader sx={{ width: 'fit-content' }}>
                        Physician Determination
                    </TypographyHeader>
                    <Typography sx={{ color: 'red', paddingLeft: '2px' }}>*</Typography>
                </Box>
            </BoxRow>
            <BoxRow sx={{ paddingBottom: '10px' }}>
                <ApprovalButtonsDecisionsContainer
                    approvalStatus={approvalStatus}
                    isNewCase={isNewCase}
                    handleApprove={handleApprove}
                    handlePartialApprove={handlePartialApprove}
                    handleDeny={handleDeny}
                    handleNoReviewRequired={handleNoReviewRequired}
                    item={item}
                    reasons={reasons}
                    handleReasonDenialChange={handleReasonDenialChange}
                />
            </BoxRow>
        </>
    );
}
export default InitialReviewProcedureItem;
